import React from "react";
import EcnInput from "../../StyledComponents/EcnInput";
import EcnCheckbox from "../../StyledComponents/EcnCheckbox";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import MobileDatePicker from "@mui/lab/MobileDatePicker";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";

import { getLocation } from "../../../redux/slices/jobSlices/getLocationSlice";
import EcnButton from "../../StyledComponents/EcnButton";
import { Icon } from "@iconify/react";

interface WorkGroupProps {
  work: any;
  index: number;
  noWorkExp: boolean;
  updateWorkExperience: (index: number, updatedWork: any) => void;
  removeWorkExperience: (index: number) => void;
  isLast: boolean;
  submitAttempted: any;
  color?: string;
  radius?: string;
}

const WorkGroup = ({
  work,
  index,
  noWorkExp,
  updateWorkExperience,
  removeWorkExperience,
  isLast,
  submitAttempted,
  color = "#0034BB",
  radius = "0px",
}: WorkGroupProps) => {
  const dispatch = useAppDispatch();
  const { locationList } = useAppSelector((state: any) => state.getLocations);

  const addressListHandler = (e: any) => {
    if (e !== null) dispatch(getLocation(e.target.value));
  };

  const handleChange = (field: string, value: any) => {
    if (field === "start_date" || field === "end_date") {
      updateWorkExperience(index, {
        [field]: value ? value : "2030-10-05T14:48:00.000Z",
      });
    } else if (field === "city") {
      if (typeof value === "string") {
        updateWorkExperience(index, { city: value });
      } else {
        updateWorkExperience(index, {
          city: `${value?.address?.municipality || ""} ${value?.address?.countrySubdivision || ""} ${value?.address?.country || ""}`.trim()
        });
      }
    } else {
      updateWorkExperience(index, { [field]: value });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      updateWorkExperience(index, {
        experience_status: 3,
        currently_working: true,
        end_date: "2030-10-05T14:48:00.000Z"
      });
    } else {
      updateWorkExperience(index, {
        experience_status: 0,
        currently_working: false,
        end_date: null
      });
    }
  };

  return (
    <div className="work-groups">
      <div className="heading">
        <h1>Experience {index + 1}</h1>
        <EcnButton
          className="remove-btn"
          color="secondary"
          style={{ color: color }}
          onClick={() => removeWorkExperience(index)}
        >
          <Icon icon={"mingcute:close-line"} color={color} />
          Remove
        </EcnButton>
      </div>

      <div className="row">
        <div className="col">
          <p>
            Title <span style={{ color: color }}>*</span>
          </p>
          <EcnInput
            className="ecn-input"
            helperText={
              submitAttempted && !work?.title ? (
                <p className="custom-error-text">Title is required</p>
              ) : null
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: radius,

                "& fieldset": {
                  borderColor: "#e4e7ec",
                },
                "&:hover fieldset": {
                  borderColor: color,
                },
                "&.Mui-focused fieldset": {
                  borderColor: color,
                },
                "& input": {
                  color: color,
                },
              },
            }}
            name="title"
            value={work?.title}
            onChange={(e: any) => handleChange("title", e.target.value)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <p>
            Organization <span style={{ color: color }}>*</span>
          </p>
          <EcnInput
            className="ecn-input"
            helperText={
              submitAttempted && !work?.company ? (
                <p className="custom-error-text">Organization is required</p>
              ) : null
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: radius,

                "& fieldset": {
                  borderColor: "#e4e7ec",
                },
                "&:hover fieldset": {
                  borderColor: color,
                },
                "&.Mui-focused fieldset": {
                  borderColor: color,
                },
                "& input": {
                  color: color,
                },
              },
            }}
            name="organisation"
            value={work?.company}
            onChange={(e: any) => handleChange("company", e.target.value)}
          />
        </div>

        <div className="col">
          <p>Location (Optional)</p>
          <Autocomplete
            freeSolo
            forcePopupIcon={false}
            disableClearable
            filterOptions={(options, state) => {
              const input = state.inputValue;
              const filtered = options.slice();
              if (input && input.trim() !== "" && !options.includes(input)) {
                filtered.push(input);
              }
              return filtered;
            }}
            className="location"
            disabled={noWorkExp}
            options={[
              ...(locationList?.results ?? []),
              ...(work?.city && typeof work?.city === "string" ? [work?.city] : [])
            ]}
            value={work?.city}
            onChange={(e, val) => {
              if (typeof val === "string") {
                handleChange("city", val);
              } else {
                handleChange("city", val);
              }
            }}
            onInputChange={addressListHandler}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option?.address
                  ? `${option.address.municipality}, ${option.address.countrySubdivision}, ${option.address.country}`
                  : ""
            }
            renderInput={(params) => (
              <EcnInput
                {...params}
                placeholder="Search location"
                size="small"
                className="ecn-input"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: radius,

                    "& fieldset": {
                      borderColor: "#e4e7ec",
                    },
                    "&:hover fieldset": {
                      borderColor: color,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: color,
                    },
                    "& input": {
                      color: color,
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option, { selected }) => {
              if (typeof option === "string") {
                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      margin: "0px 10px",
                      marginBottom: "0px !important",
                      borderRadius: "8px !important",
                      padding: "10px 15px !important",
                      fontSize: "14px !important",
                      bgcolor: props['aria-selected'] === "true"
                        ? "rgba(0, 52, 187, 0.05) !important"
                        : "transparent",
                      "&:hover": {
                        bgcolor: "rgba(0, 52, 187, 0.05) !important",
                        color: "rgba(0, 52, 187, 1)",
                      }
                    }}
                  >
                    {option}
                  </Box>
                );
              }
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    margin: "0px 10px",
                    marginBottom: "0px !important",
                    borderRadius: "8px !important",
                    padding: "10px 15px !important",
                    fontSize: "14px !important",
                    bgcolor: selected
                      ? "rgba(0, 52, 187, 0.05) !important"
                      : "transparent", // Apply the "info" color for selected options
                    "&:hover": {
                      bgcolor: "rgba(0, 52, 187, 0.05) !important", // Lighten the color on hover
                      color: "rgba(0, 52, 187, 1)",
                    },
                    color:
                      work?.city ===
                        `${option?.address?.municipality}, ${option?.address?.countrySubdivision}, ${option?.address?.country}`
                        ? "rgba(0, 52, 187, 1)"
                        : "",
                  }}
                >
                  {option?.address?.municipality},
                  {option?.address?.countrySubdivision}
                  {option?.address?.countrySubdivision ? "," : ""}
                  {option?.address?.country}
                </Box>
              );
            }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <p>Work Summary (Optional)</p>
          <TextField
            className="ecn-textarea"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: radius,

                "& fieldset": {
                  borderColor: "#e4e7ec",
                },
                "&:hover fieldset": {
                  borderColor: color,
                },
                "&.Mui-focused fieldset": {
                  borderColor: color,
                },
                "& input": {
                  color: color,
                },
              },
            }}
            multiline
            minRows={4}
            maxRows={4}
            fullWidth
            variant="outlined"
            value={work?.summary || ""}
            onChange={(e) => handleChange("summary", e.target.value)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <p>Start Date*</p>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              views={["year", "month"]}
              inputFormat="MM/yyyy"
              disabled={noWorkExp}
              maxDate={
                // work?.experience_status === 3 || work?.experience_status === 1
                //   ? new Date()
                //   : new Date("2030-01-01T00:00:00.000Z")
                new Date()
              }
              minDate={
                // work?.experience_status === 2
                //   ? new Date()
                //   :
                new Date("2000-01-01T00:00:00.000Z")
              }
              value={work?.start_date ? new Date(work?.start_date) : null}
              onChange={(date) => handleChange("start_date", date)}
              renderInput={(params) => (
                <EcnInput
                  {...params}
                  className="ecn-input"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: radius,

                      "& fieldset": {
                        borderColor: "#e4e7ec",
                      },
                      "&:hover fieldset": {
                        borderColor: color,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: color,
                      },
                      "& input": {
                        color: color,
                      },
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
          {submitAttempted && !work?.start_date ? (
            <p className="custom-error-text">Start Date is required</p>
          ) : null}
        </div>

        <div className="col">
          <p>End Date</p>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              views={["year", "month"]}
              inputFormat="MM/yyyy"
              disabled={work?.currently_working || noWorkExp}
              maxDate={
                work?.experience_status === 3
                  ? new Date()
                  : new Date("2030-01-01T00:00:00.000Z")
              }
              minDate={new Date(work?.start_date)}
              value={
                work?.currently_working
                  ? null
                  : work?.end_date &&
                    work.end_date !== "2030-10-05T14:48:00.000Z"
                    ? new Date(work?.end_date)
                    : null
              }
              onChange={(date) => handleChange("end_date", date)}
              renderInput={(params) => (
                <EcnInput
                  {...params}
                  className="ecn-input"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: radius,

                      "& fieldset": {
                        borderColor: "#e4e7ec",
                      },
                      "&:hover fieldset": {
                        borderColor: color,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: color,
                      },
                      "& input": {
                        color: color,
                      },
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
          <FormControlLabel
            className="work-label"
            control={
              <EcnCheckbox
                sx={{ color: color }}
                checked={work?.currently_working || false}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            sx={{
              ".MuiFormControlLabel-label": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#1D2939",
              },
            }}
            label="I am currently working here"
          />
          {
            (
              (
                submitAttempted && !work?.currently_working && (!work?.end_date || work?.end_date === "2030-10-05T14:48:00.000Z")
              ) ||
              (
                submitAttempted && !work?.currently_working && (!work?.end_date || work.end_date === "2030-10-05T14:48:00.000Z")
              )
            ) ? (
              <p className="custom-error-text">End Date or Currently Working is required</p>
            ) : null
          }
        </div>
      </div>
    </div>
  );
};

export default WorkGroup;
